import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { BetaProgramItem, BetaProgramsEvents, GetBetaProgramsResponse } from '../types/types';
import { BetaProgramSection } from '../innerComponents/betaProgramSection';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzRadioGroupWebComponent } from '../../../../ui/gzRadio';
import {
  WEB_COMPONENT_BETA_PROGRAM_SECTION,
  WEB_COMPONENT_GZ_INFO_BOX,
  WEB_COMPONENT_GZ_RADIO_GROUP,
} from '../../../../webcomponents.constants';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { ImageState } from '../../../../ui/gzImageRepo/types';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { upperCase } from 'lodash';
import { InfoBoxSeverity } from '../../../../ui/gzInfoBox/types';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { FontSizes } from '../../../../types/common/types';

export class CompanyBetaProgramsTabView extends BaseWebComponentView {
  private betaProgramItems: Array<BetaProgramItem>;

  public dom: {
    $wrapper: HTMLElement;
    $sectionDescription: HTMLElement;
    $descriptionText: GzTextWebComponent;
    $descriptionLink: GzLinkWebComponent;
    $betaTitle: GzTitleWebComponent;
    $statusTitle: GzTitleWebComponent;
    $buttonsContainer: HTMLElement;
    $warningMessage: GzInfoBoxWebComponent;
    $betaProgramsSection: BetaProgramSection;
    $enrollButton: GzButtonWebComponent;
    $rolloutButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $sectionDescription: this.shadow.querySelector('#section-description'),
      $descriptionText: this.shadow.querySelector('#description-text'),
      $descriptionLink: this.shadow.querySelector('#description-link'),
      $betaTitle: this.shadow.querySelector('#beta-title'),
      $statusTitle: this.shadow.querySelector('#status-title'),
      $buttonsContainer: this.shadow.querySelector('#buttons-container'),
      $betaProgramsSection: this.shadow.querySelector('#beta-programs'),
      $enrollButton: this.shadow.querySelector('#enroll-button'),
      $rolloutButton: this.shadow.querySelector('#rollout-button'),
      $warningMessage: this.shadow.querySelector(WEB_COMPONENT_GZ_INFO_BOX),
    };
  }

  buildSuccessView(data: GetBetaProgramsResponse) {
    this.betaProgramItems = data.availablePrograms;

    if (data.availablePrograms.length < 1) {
      this.buildEmptyView();
      return;
    }

    this.buildBetaSection();
  }

  buildBetaSection() {
    this.buildDescriptionSection();

    this.dom.$betaProgramsSection.betaProgramItems = this.betaProgramItems;
    this.dom.$betaProgramsSection.enrolledStatusText = this.dependencies.translateFn('Enrolled');
    this.dom.$betaProgramsSection.notEnrolledStatusText = this.dependencies.translateFn('Not enrolled');
    this.dom.$betaProgramsSection.learnMoreText = this.dependencies.translateFn('Learn more');

    const enrollButton = this.dom.$enrollButton;
    enrollButton.innerHTML = this.dependencies.translateFn('enroll');
    enrollButton.disabled = true;
    enrollButton.onclick = () => this.attemptEnrollmentChange(BetaProgramsEvents.ENROLL);

    const rolloutButton = this.dom.$rolloutButton;
    rolloutButton.innerHTML = this.dependencies.translateFn('opt out');
    rolloutButton.disabled = true;
    rolloutButton.onclick = () => this.attemptEnrollmentChange(BetaProgramsEvents.OPTOUT);

    this.dom.$betaTitle.innerText = upperCase(this.dependencies.translateFn('Available programs'));
    this.dom.$statusTitle.innerText = upperCase(this.dependencies.translateFn('Status'));

    this.dom.$warningMessage.severity = InfoBoxSeverity.WARNING;
  }

  private buildDescriptionSection() {
    const descriptionText = this.dom.$descriptionText;
    descriptionText.innerHTML = this.dependencies.translateFn(
      'Bitdefender Early Access allows you to test out specific GravityZone features or Bitdefender products prior to being released. Access is gained by enrolling to specific programs, which may include changes to available features, functionality, and user interface. Enrollment to a specific program may affect the normal functioning of your Bitdefender products.'
    );

    const learnMoreGzLink = this.dom.$descriptionLink;
    learnMoreGzLink.href = 'https://www.bitdefender.com/business/support/en/77211-214891-early-access-programs.html';
    learnMoreGzLink.innerHTML = `<span>${this.dependencies.translateFn('Learn more.')}</span>`;
    descriptionText.appendChild(learnMoreGzLink);
  }

  public switchButtonsState(selectedValue: string) {
    if (!this.dom.$enrollButton || !this.dom.$rolloutButton || !selectedValue) return;

    const selectedBetaProgram = this.getBetaProgramByType(selectedValue);

    this.dom.$enrollButton.disabled = selectedBetaProgram.enrolled;
    this.dom.$rolloutButton.disabled = !selectedBetaProgram.enrolled;
  }

  public getBetaProgramByType(type: string) {
    return this.betaProgramItems.filter((item: BetaProgramItem) => item.type === parseInt(type, 10))[0];
  }

  private attemptEnrollmentChange(eventName: string) {
    const rbGroup: GzRadioGroupWebComponent = this.shadow
      .querySelector(`${WEB_COMPONENT_BETA_PROGRAM_SECTION}`)
      .shadowRoot.querySelector(`${WEB_COMPONENT_GZ_RADIO_GROUP}`);

    const selectedProgram = rbGroup.selectedOption;

    const event = new CustomEvent(BetaProgramsEvents.ATTEMPT_ENROLLMENT, {
      detail: {
        selectedProgram,
        eventName,
      },
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    return this.dom.$buttonsContainer.dispatchEvent(event);
  }

  public createDialog(eventName: string, dialogConfirmCallback: () => void): GzDialogWebComponent {
    const bodyText =
      eventName === BetaProgramsEvents.OPTOUT
        ? this.dependencies.translateFn(
            'Your company will lose access to all the feature, functionality, and interface changes included in the program.'
          )
        : this.dependencies.translateFn(
            'Your company will gain access to all the feature, functionality, and interface changes included in the program.'
          );

    const confirmationButtonText =
      eventName === BetaProgramsEvents.OPTOUT
        ? this.dependencies.translateFn('Opt out')
        : this.dependencies.translateFn('Enroll');

    const dialogBody = `<p>${bodyText}</p>`;

    const dialogFooter = `
        <div id='optout-dialog-buttons-container'>
            <gz-button id='confirm'>${confirmationButtonText}</gz-button>
            <gz-button id='cancel' type='outline' color='secondary'>${this.dependencies.translateFn(
              'Cancel'
            )}</gz-button>
        </div>
    `;

    const headline =
      eventName === BetaProgramsEvents.OPTOUT
        ? this.dependencies.translateFn('Opt out of program?')
        : this.dependencies.translateFn('Enroll in program?');

    return GzDialogWebComponent.computeCustomDialog(
      dialogBody,
      dialogFooter,
      headline,
      null,
      dialogConfirmCallback,
      null,
      false
    );
  }

  buildEmptyView() {
    const noBetaProgramsText = this.dependencies.translateFn(
      'There are no Early Access programs currently {break} available for your company.',
      {
        break: '<br>',
      }
    );
    const noBetaProgramsTitle = this.dependencies.translateFn('No programs available');

    const learnMoreGzLink = 'https://www.bitdefender.com/business/support/en/77211-214891-early-access-programs.html';
    const learnMoreGzTextLink = `<span>${this.dependencies.translateFn('Learn more.')}</span>`;

    this.dom.$wrapper.innerHTML = `
        <gz-image-repo state="${ImageState.EMPTY}">
          <gz-title fontSize='${FontSizes.HEADING_4}' class="emptyViewTitle" fontWeight="500">${noBetaProgramsTitle}</gz-title>
          <gz-text class="emptyViewText" fontSize="13">${noBetaProgramsText}<gz-link class="emptyViewLink" href="${learnMoreGzLink}">${learnMoreGzTextLink}</gz-link></gz-text>
        </gz-image-repo>`;
  }
}
